import { Helmet } from "react-helmet-async";
import { Button } from "@mineoptics/shared-ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@mineoptics/shared-ui";
import { Bomb, Zap, Users, TrendingDown, Target, ArrowRight, CheckCircle } from "lucide-react";

const features = [
  {
    icon: Zap,
    title: "Explosives Management",
    description: "Track ANFO, emulsion, boosters, and detonators with inventory control and usage optimization."
  },
  {
    icon: Users,
    title: "Blast Crew Labor",
    description: "Manage blast crew costs including certified blasters, assistants, and supervision requirements."
  },
  {
    icon: TrendingDown,
    title: "Equipment Depreciation",
    description: "Calculate depreciation costs for blasting equipment with customizable schedules and replacement planning."
  },
  {
    icon: Target,
    title: "Blast Design Optimization",
    description: "Optimize blast patterns and powder factors to minimize costs while maintaining fragmentation targets."
  }
];

export default function Blasting() {
  const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.mineoptics.com';

  return <>
    <Helmet>
      <title>Blasting Cost Model | MineOptics</title>
      <meta
        name="description"
        content="Complete blasting economics covering explosives, detonators, crew labor, and equipment depreciation for optimized blast design."
      />
    </Helmet>

    <div className="min-h-screen bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
          <a href="/" className="flex items-center gap-2.5 group">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-white font-bold text-sm shadow-lg shadow-primary/25 transition-transform group-hover:scale-105">
              M
            </div>
            <span className="font-semibold text-xl tracking-tight">MineOptics</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/#features" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1">Features</a>
            <a href="/pricing" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1">Pricing</a>
            <a href="https://docs.mineoptics.com" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1" target="_blank" rel="noopener noreferrer">Docs</a>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="hidden sm:inline-flex font-medium" asChild>
              <a href={`${APP_URL}/auth/login`}>Sign in</a>
            </Button>
            <Button className="btn-gradient text-white border-0 shadow-lg shadow-primary/25 font-medium" asChild>
              <a href={`${APP_URL}/auth/signup`}>Get Started<ArrowRight className="ml-2 h-4 w-4" /></a>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative pt-32 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 gradient-bg opacity-[0.03]" />
        <div className="relative container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
              <Bomb className="w-4 h-4" />
              <span>Blasting Cost Model</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Comprehensive Blasting<br /><span className="gradient-text">Economics</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Complete blasting economics covering explosives, detonators, crew labor, and equipment depreciation
              with optimization tools for blast design and cost efficiency.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="btn-gradient text-white border-0 shadow-xl shadow-primary/30 font-medium text-base px-8 h-12" asChild>
                <a href={`${APP_URL}/auth/signup`}>Start Free Trial<ArrowRight className="ml-2 h-5 w-5" /></a>
              </Button>
              <Button size="lg" variant="outline" className="font-medium text-base px-8 h-12 bg-white/50 backdrop-blur-sm hover:bg-white/80" asChild>
                <a href="https://docs.mineoptics.com" target="_blank" rel="noopener noreferrer">View Documentation</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card key={index} className="border-0 shadow-soft bg-white/80 backdrop-blur-sm hover-lift">
                    <CardHeader className="pb-6 pt-8 px-8">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center mb-5">
                        <Icon className="w-7 h-7 text-primary" />
                      </div>
                      <CardTitle className="text-xl font-semibold mb-3">{feature.title}</CardTitle>
                      <CardDescription className="text-base leading-relaxed">{feature.description}</CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 gradient-bg-subtle">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why Choose Our Blasting Module?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Blast Pattern Optimization</h3>
                  <p className="text-muted-foreground">Optimize burden, spacing, and powder factors to achieve target fragmentation at minimum cost.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Explosives Inventory Management</h3>
                  <p className="text-muted-foreground">Track explosives usage and inventory levels with automated reorder alerts and supplier management.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Cost-Per-Tonne Analysis</h3>
                  <p className="text-muted-foreground">Calculate and track blasting costs per tonne moved with detailed breakdowns by blast and bench.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Regulatory Compliance</h3>
                  <p className="text-muted-foreground">Built-in compliance tracking for explosives handling, storage, and usage reporting requirements.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 gradient-bg" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        <div className="relative container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">Ready to optimize your blasting costs?</h2>
            <p className="text-lg md:text-xl mb-10 text-white/80">Start your free trial today and transform your blasting operations</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 font-medium text-base px-8 h-12 shadow-xl" asChild>
                <a href={`${APP_URL}/auth/signup`}>Start Free Trial<ArrowRight className="ml-2 h-5 w-5" /></a>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-medium text-base px-8 h-12 bg-transparent" asChild>
                <a href="/contact">Contact Sales</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-16 md:py-20 bg-muted/30 border-t">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-5 gap-12 md:gap-8">
            <div className="md:col-span-2">
              <a href="/" className="flex items-center gap-2.5 mb-6">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-white font-bold text-sm shadow-lg shadow-primary/25">M</div>
                <span className="font-semibold text-xl tracking-tight">MineOptics</span>
              </a>
              <p className="text-muted-foreground max-w-sm leading-relaxed">The modern platform for mining cost modeling. Built for teams who want to work smarter, not harder.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Product</h3>
              <ul className="space-y-3">
                <li><a href="/#features" className="text-muted-foreground hover:text-primary transition-colors">Features</a></li>
                <li><a href="/pricing" className="text-muted-foreground hover:text-primary transition-colors">Pricing</a></li>
                <li><a href="https://docs.mineoptics.com" className="text-muted-foreground hover:text-primary transition-colors" target="_blank" rel="noopener noreferrer">Documentation</a></li>
                <li><a href={`${APP_URL}`} className="text-muted-foreground hover:text-primary transition-colors">Dashboard</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Company</h3>
              <ul className="space-y-3">
                <li><a href="/contact" className="text-muted-foreground hover:text-primary transition-colors">Contact</a></li>
                <li><a href="/about" className="text-muted-foreground hover:text-primary transition-colors">About</a></li>
                <li><a href="/careers" className="text-muted-foreground hover:text-primary transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Legal</h3>
              <ul className="space-y-3">
                <li><a href="/privacy" className="text-muted-foreground hover:text-primary transition-colors">Privacy</a></li>
                <li><a href="/terms" className="text-muted-foreground hover:text-primary transition-colors">Terms</a></li>
                <li><a href="/security" className="text-muted-foreground hover:text-primary transition-colors">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t">
            <p className="text-sm text-muted-foreground text-center">© 2024 MineOptics. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  </>;
}
